/**
 * Spec-sheet send-state model (R14 B2/B3 — close-card send truthfulness + voice-commit parity).
 *
 * Two field learnings drive this (third on-kiosk session, dev-75):
 *
 *  B2 — the visitor said "it's right" and the order committed by VOICE, but the kiosk still showed
 *  the order-review card waiting for a tap. R31 §12 adds `ui.order_committed {order_id, items}`: the
 *  server acks a voice-confirmed commit so the kiosk advances to the close card exactly as if Confirm
 *  had been tapped (voice-or-tap parity — the same peer discipline as the welcome card).
 *
 *  B3 — the close card must never show a false "on its way". R31 §12 adds a `no_order` send state:
 *  when `send_spec_sheet` (or the close-card send path) is attempted with no committed order this
 *  session, the server returns `no_order` and the kiosk shows "confirm your selection first" (with a
 *  back-to-review affordance) — never a spec-sheet-sent confirmation. A genuine send still acks via
 *  `ui.contact_ack {persisted}` (§6, unchanged) into the sent/queued state.
 *
 * Contract note (tolerant): the R31 server half may not have frozen the exact wire shapes yet. These
 * parsers accept BOTH plausible nestings (top-level or nested `payload`) and tolerate key aliases —
 * the same seam discipline that fixed `ui.categories` / `ui.contact_field`. When R31 freezes ONE
 * shape the kiosk binds to it with no change.
 *
 * Pure (no React / no DOM) so parsing is unit-testable.
 */

/**
 * The close-card / spec-sheet send state, richer than the plain `closeEmailAck` boolean:
 *   - null      → not submitted yet / in-flight (spinner)
 *   - 'sent'    → persisted + delivered/queued (the truthful "on its way")
 *   - 'queued'  → persisted but delivery deferred (Resend not yet provisioned)
 *   - 'no_order'→ send attempted with NO committed order this session — "confirm your selection
 *                 first" (never a false confirmation). B3.
 *   - 'error'   → backend rejected the submit (not a no-order case)
 */
export type SendState = null | 'sent' | 'queued' | 'no_order' | 'error';

/**
 * Reduce the legacy `ui.contact_ack {persisted}` boolean into a SendState. `persisted:true` →
 * 'sent'; `persisted:false` → 'queued' (send deferred). This preserves the R12/R13 behaviour where
 * ack===false meant "queued" (delivery deferred), while giving `no_order` a distinct channel below.
 */
export function sendStateFromContactAck(persisted: boolean): SendState {
  return persisted ? 'sent' : 'queued';
}

type RawOrderCommitted = {
  order_id?: unknown;
  orderId?: unknown;
  id?: unknown;
  items?: unknown;
  payload?: unknown;
};

/** A committed order the kiosk advances the review scene against (from `ui.order_committed`). */
export type OrderCommitted = {
  orderId?: string;
  items: Array<{ sku: string; qty: number }>;
};

/**
 * Parse a `ui.order_committed` message (R31 §12 — voice-confirmed commit) into an OrderCommitted.
 * Tolerant of nesting (`order_id`|`orderId`|`id` at the top level OR under `payload`) and of the
 * item shape (`sku`|`id`, `qty`|`quantity`). Returns null only when the message carries nothing
 * usable — but the kiosk should still advance the scene on receipt (the commit already happened
 * server-side), so callers treat a non-null OR a bare `{type:'ui.order_committed'}` as "advance".
 */
export function parseOrderCommitted(msg: unknown): OrderCommitted | null {
  if (!msg || typeof msg !== 'object') return null;
  const outer = msg as RawOrderCommitted;
  const nested =
    outer.payload && typeof outer.payload === 'object'
      ? (outer.payload as RawOrderCommitted)
      : null;
  const idRaw =
    firstString(outer.order_id, outer.orderId, outer.id) ??
    (nested ? firstString(nested.order_id, nested.orderId, nested.id) : undefined);
  const itemsRaw = Array.isArray(outer.items)
    ? outer.items
    : nested && Array.isArray(nested.items)
      ? nested.items
      : [];
  const items: Array<{ sku: string; qty: number }> = [];
  for (const it of itemsRaw) {
    if (!it || typeof it !== 'object') continue;
    const o = it as { sku?: unknown; id?: unknown; qty?: unknown; quantity?: unknown };
    const sku = firstString(o.sku, o.id);
    if (!sku) continue;
    const qtyRaw =
      typeof o.qty === 'number' ? o.qty : typeof o.quantity === 'number' ? o.quantity : 1;
    const qty = Math.max(1, Math.min(999, Math.round(qtyRaw)));
    items.push({ sku, qty });
  }
  return { orderId: idRaw, items };
}

type RawSpecSheetState = {
  state?: unknown;
  status?: unknown;
  spec_sheet_send_state?: unknown; // R31 §12.5 — the frozen close-card send-state key
  specSheetSendState?: unknown;
  no_order?: unknown; // R31 §12.3 — the orderless-guard flag (boolean, NOT a state string)
  noOrder?: unknown;
  persisted?: unknown;
  payload?: unknown;
};

/**
 * Parse a `ui.contact_ack` / spec-sheet-send message into a SendState, honouring the R31 §12 shapes
 * VERBATIM plus tolerant aliases. Precedence (highest first):
 *   1. the `no_order` BOOLEAN flag (R31 §12.3 tool response `{prepared:false, no_order:true}`,
 *      surfaced via the close-card send-state, §12.5) — the orderless guard wins over everything so a
 *      refusal is never read as a persisted send;
 *   2. an explicit state string on `state` / `status` / `spec_sheet_send_state` ('no_order' | 'sent'
 *      | 'queued' | 'error');
 *   3. else the legacy `persisted` boolean (`true`→'sent', `false`→'queued').
 * Tolerant of nesting (fields at the top level OR under `payload`). Returns undefined when the
 * message carries none of the above (caller keeps the prior state).
 */
export function parseSendState(msg: unknown): SendState | undefined {
  if (!msg || typeof msg !== 'object') return undefined;
  const outer = msg as RawSpecSheetState;
  const nested =
    outer.payload && typeof outer.payload === 'object'
      ? (outer.payload as RawSpecSheetState)
      : null;
  // 1 — the R31 §12.3 orderless guard: a `no_order: true` boolean anywhere. Highest precedence.
  if (outer.no_order === true || outer.noOrder === true) return 'no_order';
  if (nested && (nested.no_order === true || nested.noOrder === true)) return 'no_order';
  // 2 — an explicit state string (`state` / `status` / `spec_sheet_send_state`, §12.5).
  const stateStr = firstString(
    outer.state,
    outer.status,
    outer.spec_sheet_send_state,
    outer.specSheetSendState,
    nested?.state,
    nested?.status,
    nested?.spec_sheet_send_state,
    nested?.specSheetSendState
  );
  if (stateStr) {
    const s = stateStr.toLowerCase();
    if (s === 'no_order' || s === 'no-order' || s === 'noorder') return 'no_order';
    if (s === 'sent' || s === 'delivered') return 'sent';
    if (s === 'queued' || s === 'deferred') return 'queued';
    if (s === 'error' || s === 'failed') return 'error';
  }
  // 3 — the legacy `persisted` boolean.
  const persisted =
    typeof outer.persisted === 'boolean'
      ? outer.persisted
      : typeof nested?.persisted === 'boolean'
        ? nested.persisted
        : undefined;
  if (typeof persisted === 'boolean') return sendStateFromContactAck(persisted);
  return undefined;
}

function firstString(...vals: unknown[]): string | undefined {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim() !== '') return v.trim();
  }
  return undefined;
}
